// src/pages/FulfillmentPage/components/AddressCard.tsx
import React from "react";
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  Avatar,
  alpha,
  useTheme,
  Paper,
  Divider,
  Chip
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HomeIcon from "@mui/icons-material/Home";
import { Address, Fulfillment } from "../types";

interface AddressCardProps {
  address: Address | null;
  fulfillment: Fulfillment;
  isLocalDelivery: boolean;
}

export const AddressCard: React.FC<AddressCardProps> = ({ address, fulfillment, isLocalDelivery }) => {
  const theme = useTheme();
  
  if (!address) return null;

  // Helper to get address type icon and color
  const getAddressTypeInfo = () => {
    if (isLocalDelivery) {
      return {
        icon: <LocalShippingIcon />,
        color: theme.palette.warning.main,
        label: "Delivery"
      };
    }
    return {
      icon: <HomeIcon />,
      color: theme.palette.info.main,
      label: "Shipping"
    };
  };

  const addressType = getAddressTypeInfo();

  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: 3, 
        mb: 2, 
        width: "100%", 
        overflow: "hidden",
        border: '1px solid',
        borderColor: theme.palette.divider,
        bgcolor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(addressType.color, 0.3),
          boxShadow: `0 4px 12px ${alpha(addressType.color, 0.1)}`
        }
      }}
    >
      {/* Header with colored accent */}
      <Box 
        sx={{ 
          height: 4, 
          width: '100%', 
          bgcolor: addressType.color,
          opacity: 0.8
        }} 
      />

      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        {/* Title with icon */}
        <Stack 
          direction="row" 
          alignItems="center" 
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(addressType.color, 0.1),
                color: addressType.color,
              }}
            >
              {addressType.icon}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {isLocalDelivery ? "Delivery Address" : "Shipping Address"}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {addressType.label} details for order #{fulfillment.order_number}
              </Typography>
            </Box>
          </Stack>
          
          <Chip
            label={addressType.label}
            size="small"
            sx={{
              bgcolor: alpha(addressType.color, 0.1),
              color: addressType.color,
              fontWeight: 600,
              borderRadius: 1.5,
              height: 24
            }}
          />
        </Stack>

        {/* Address Details Grid */}
        <Stack spacing={2.5}>
          {/* Name */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                color: theme.palette.text.secondary,
                flexShrink: 0
              }}
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box sx={{ width: "100%", overflow: "hidden" }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 0.5,
                  display: 'block'
                }}
              >
                Recipient Name
              </Typography>
              <Typography 
                sx={{ 
                  fontWeight: 600, 
                  wordBreak: "break-word",
                  fontSize: '1rem',
                  color: theme.palette.text.primary
                }}
              >
                {address.fullName}
              </Typography>
            </Box>
          </Box>

          {/* Address Lines */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                color: theme.palette.text.secondary,
                flexShrink: 0
              }}
            >
              <LocationOnIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box sx={{ width: "100%", overflow: "hidden" }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 0.5,
                  display: 'block'
                }}
              >
                Street Address
              </Typography>
              <Typography
                sx={{
                  fontWeight: 500,
                  wordBreak: "break-word",
                  whiteSpace: "pre-line",
                  color: theme.palette.text.primary,
                  mb: 0.5
                }}
              >
                {address.line1}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  wordBreak: "break-word"
                }}
              >
                {address.location}
              </Typography>
            </Box>
          </Box>

          {/* Contact Info Grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mt: 1
          }}>
            {/* Phone */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  color: theme.palette.success.main,
                }}
              >
                <PhoneIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Box sx={{ width: "100%", overflow: "hidden" }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                  Phone
                </Typography>
                <Typography sx={{ fontWeight: 500, wordBreak: "break-word", fontSize: '0.9rem' }}>
                  {address.phone}
                </Typography>
              </Box>
            </Box>

            {/* Email */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  color: theme.palette.info.main,
                }}
              >
                <EmailIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Box sx={{ width: "100%", overflow: "hidden" }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                  Email
                </Typography>
                <Typography 
                  sx={{ 
                    fontWeight: 500, 
                    wordBreak: "break-all", 
                    fontSize: '0.9rem',
                    color: theme.palette.text.primary
                  }}
                >
                  {address.email}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Delivery Fee (if local delivery) */}
          {isLocalDelivery && (
            <>
              <Divider sx={{ my: 1 }} />
              
              <Box 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.warning.main, 0.1)
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocalShippingIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Delivery Fee
                  </Typography>
                </Stack>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.warning.main,
                    fontSize: '1.25rem'
                  }}
                >
                  ${(Number(fulfillment.delivery_fee_cents) / 100).toFixed(2)}
                </Typography>
              </Box>
            </>
          )}

          {/* Map Link (if address has coordinates - optional) */}
          {address.line1 !== "N/A" && (
            <Box sx={{ mt: 1 }}>
              <Typography 
                component="a"
                href={`https://maps.google.com/?q=${encodeURIComponent(`${address.line1} ${address.location}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                <LocationOnIcon sx={{ fontSize: 14 }} />
                View on Google Maps
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Paper>
  );
};